import React, { useReducer } from 'react';
import { string, shape, arrayOf, bool, number, object } from 'prop-types';
import postal from 'postal';
import Router from 'components/Router';
import MinimalLayout from 'components/MinimalLayout';
import { contextPropTypes } from 'proptypes/shared';
import { Page } from 'components/shared/Page/Page';
import GiftFlowRouting from '../GiftFlowRouting';

const GiftFlowContext = React.createContext({});

const initialState = {
  activePlanType: 'annualOnce',
  giftCustomizeParams: {
    backgroundId: 'gc-1',
    sendMethod: 'email',
    sendDate: new Date(),
    recipientName: '',
    recipientEmail: '',
    message: '',
    senderName: '',
    senderEmail: ''
  },
  serverErrors: [],
  purchasedGiftCard: null
};

// actions
const TOGGLE_PLAN_TYPE = 'TOGGLE_PLAN_TYPE';
const UPDATE_GIFT_CUSTOMIZE_FORM = 'UPDATE_GIFT_CUSTOMIZE_FORM';
const UPDATE_SERVER_ERRORS = 'UPDATE_SERVER_ERRORS';
const UPDATE_PURCHASED_GIFT_CARD = 'UPDATE_PURCHASED_GIFT_CARD';
const RESTART_GIFT_FLOW = 'RESTART_GIFT_FLOW';

const giftFlowReducer = (state, action) => {
  switch (action.type) {
    case TOGGLE_PLAN_TYPE: {
      return { ...state, activePlanType: action.payload };
    }
    case UPDATE_GIFT_CUSTOMIZE_FORM: {
      return { ...state, giftCustomizeParams: action.payload };
    }
    case UPDATE_SERVER_ERRORS: {
      return { ...state, serverErrors: action.payload };
    }
    case UPDATE_PURCHASED_GIFT_CARD:
      return { ...state, purchasedGiftCard: action.payload };
    case RESTART_GIFT_FLOW:
      return { ...state, ...initialState };
    default:
      return state;
  }
};

const navigationMessagingChannel = postal.channel('navigation_messagingChannel');

const GiftFlow = ({
  currencyCode,
  facebookAppId,
  amazonClientId,
  amazonSellerId,
  paymentTypes,
  giftSubscription,
  shopifyProducts,
  enableRecaptcha,
  planTypes,
  recurlyPublicKey,
  fullpath,
  context,
  valentinesDayVariant
}) => {
  const [giftFlowState, dispatchGiftFlow] = useReducer(giftFlowReducer, {
    ...initialState,
    valentinesDayVariant,
    purchasedGiftCard: giftSubscription
  });

  return (
    <Page context={context}>
      <MinimalLayout>
        {/* child styles depend upon a wrapping div */}
        <div>
          <GiftFlowContext.Provider value={{ giftFlowState, dispatchGiftFlow }}>
            <Router fullpath={fullpath}>
              <GiftFlowRouting
                facebookAppId={facebookAppId}
                planTypes={planTypes}
                currencyCode={currencyCode}
                paymentTypes={paymentTypes}
                recurlyPublicKey={recurlyPublicKey}
                amazonClientId={amazonClientId}
                amazonSellerId={amazonSellerId}
                navigationMessagingChannel={navigationMessagingChannel}
                enableRecaptcha={enableRecaptcha}
                shopifyProducts={shopifyProducts}
              />
            </Router>
          </GiftFlowContext.Provider>
        </div>
      </MinimalLayout>
    </Page>
  );
};

GiftFlow.propTypes = {
  currencyCode: string,
  facebookAppId: number.isRequired,
  amazonClientId: string.isRequired,
  amazonSellerId: string.isRequired,
  paymentTypes: arrayOf(
    shape({
      name: string,
      showIcon: bool,
      showTab: bool,
      tab: number
    })
  ).isRequired,
  giftSubscription: object,
  enableRecaptcha: bool,
  planTypes: arrayOf(
    shape({
      allowCoupon: bool,
      numberOfMonths: string,
      priceMonth: number,
      priceTotal: number,
      showDiscount: bool
    })
  ).isRequired,
  recurlyPublicKey: string.isRequired,
  fullpath: string.isRequired,
  context: contextPropTypes.isRequired,
  valentinesDayVariant: bool,
  shopifyProducts: arrayOf(shape({ images: arrayOf(shape({ src: string, alt: string })), title: string, shopItemUrl: string, price: string }))
};

GiftFlow.defaultProps = {
  currencyCode: 'USD',
  giftSubscription: null,
  enableRecaptcha: false,
  valentinesDayVariant: false,
  shopifyProducts: []
};

export {
  GiftFlow,
  GiftFlowContext,
  TOGGLE_PLAN_TYPE,
  UPDATE_GIFT_CUSTOMIZE_FORM,
  UPDATE_SERVER_ERRORS,
  UPDATE_PURCHASED_GIFT_CARD,
  RESTART_GIFT_FLOW
};
